import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import './OurMenu.css';
import './CafeMenu.css';
import Footer from '../Footer';
import { db, functions } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Default menu — used ONLY as a fallback if Firestore is empty.
// Once you save from /admin-menu, that data is what the page shows.
// Prices are strings (parsed by priceToCents). For items whose
// description has both an origin line and tasting notes, put the
// origin on line 1 and the notes on line 2 (separated by \n).
export const DEFAULT_MENU = [
    {
        category: 'Coffee',
        items: [
            { name: 'Americano', desc: '', price: '$5.50' },
            { name: 'Caffè Latte', desc: '', price: '$5.50' },
            { name: 'Cappuccino', desc: '', price: '$5.50' },
            { name: 'Mocha', desc: '', price: '$5.50' },
            { name: 'Coconut Latte', desc: '', price: '$6.00' },
            { name: 'Cold Brew (Peru)', desc: '', price: '$6.00' },
            { name: 'Nitro Cold Brew (Peru)', desc: '', price: '$6.00' },
        ],
    },
    {
        category: 'Pour-Over',
        items: [
            {
                name: 'La Bastilla Geisha',
                desc: 'Nicaragua · Anaerobic Geisha · Light\nJasmine, white peach, passion fruit, bergamot',
                price: '$11.00',
            },
            {
                name: 'Mokha Matari',
                desc: 'Yemen · Natural Typica · Medium Dark\nDark chocolate, winey blackberry, cardamom, exotic spice',
                price: '$9.00',
            },
            {
                name: 'La Paz Apolo',
                desc: 'Bolivia · Washed Typica & Red Catuai · Medium\nMilk chocolate, red apple, brown sugar, crisp citrus',
                price: '$6.00',
            },
        ],
    },
    {
        category: 'Non-Coffee',
        items: [
            { name: 'Bottle Water', desc: '', price: '$2.00' },
            { name: 'Soda', desc: '', price: '$2.50' },
            { name: 'Coconut Water', desc: '', price: '$2.50' },
        ],
    },
    {
        category: 'Cast Iron Sandwiches',
        items: [
            { name: 'Nutella Banana', desc: '', price: '$5.00' },
            { name: 'Peanut Butter', desc: '', price: '$5.00' },
            { name: 'Ham & Cheese', desc: '', price: '$5.50' },
        ],
    },
];

const DEFAULT_MILKS = [
    { name: 'Whole', available: true },
    { name: 'Oat', available: true },
];

// Parse "$5.00" / "$16" → integer cents. Returns null for blank,
// "from $18", or anything that isn't a clean price.
function priceToCents(str) {
    if (typeof str !== 'string') return null;
    const trimmed = str.trim().replace(/^\$/, '');
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
    return Math.round(parseFloat(trimmed) * 100);
}

// Only the "Coffee" section gets the Hot/Iced dropdown. Every other
// section (Pour-Over, Non-Coffee, Sandwiches, any custom section
// admin adds) shows a single +Add button.
const isCoffeeSection = (category) =>
    /^\s*coffee\s*$/i.test(String(category || ''));

const isHotOnly = (name) => /cappuccino|espresso/i.test(name);
const isColdOnly = (name) => /cold brew|nitro/i.test(name);
// Coconut Latte already specifies its milk in the name — no whole/oat selector.
const isMilkDrink = (name) =>
    /latte|cappuccino|mocha/i.test(name) && !/coconut/i.test(name);
const money = (cents) => `$${(cents / 100).toFixed(2)}`;

function CafeMenu() {
    const navigate = useNavigate();
    const [menu, setMenu] = useState(DEFAULT_MENU);
    const [milkOptions, setMilkOptions] = useState(DEFAULT_MILKS);
    const [order, setOrder] = useState([]); // { name, temp, milk, unit, quantity }
    const [showCheckout, setShowCheckout] = useState(false);
    const [milkChoice, setMilkChoice] = useState({});
    const [tempChoice, setTempChoice] = useState({}); // { [itemName]: 'Hot' | 'Iced' }
    const [custName, setCustName] = useState('');
    const [custEmail, setCustEmail] = useState('');
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = 'Coffee & Food Menu | OFFF THE ROAST';
    }, []);

    // Live-sync from Firestore. Anything saved in /admin-menu shows here.
    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, 'siteContent', 'menu'),
            (snap) => {
                const data = snap.data();
                if (data && Array.isArray(data.sections) && data.sections.length > 0) {
                    setMenu(data.sections);
                } else {
                    setMenu(DEFAULT_MENU);
                }
                if (data && Array.isArray(data.milkOptions) && data.milkOptions.length > 0) {
                    setMilkOptions(
                        data.milkOptions
                            .filter((m) => m && m.name)
                            .map((m) => ({ name: m.name, available: m.available !== false }))
                    );
                } else {
                    setMilkOptions(DEFAULT_MILKS);
                }
            },
            () => setMenu(DEFAULT_MENU)
        );
        return () => unsub();
    }, []);

    const addToOrder = (name, unit, temp, milk = null) => {
        setOrder((prev) => {
            const i = prev.findIndex(
                (x) => x.name === name && x.temp === temp && (x.milk || null) === (milk || null)
            );
            if (i >= 0) {
                const next = [...prev];
                next[i] = { ...next[i], quantity: next[i].quantity + 1 };
                return next;
            }
            return [...prev, { name, temp, milk, unit, quantity: 1 }];
        });
    };

    const changeQty = (idx, delta) => {
        setOrder((prev) => {
            const next = [...prev];
            const q = next[idx].quantity + delta;
            if (q <= 0) next.splice(idx, 1);
            else next[idx] = { ...next[idx], quantity: q };
            return next;
        });
    };

    const orderCount = order.reduce((s, x) => s + x.quantity, 0);
    const orderTotal = order.reduce((s, x) => s + x.unit * x.quantity, 0);

    const validateForm = () => {
        if (!custName.trim()) return 'Please enter your name.';
        if (custEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(custEmail.trim())) {
            return 'Please enter a valid email, or leave it blank.';
        }
        if (order.length === 0) return 'Your order is empty.';
        return '';
    };

    const itemsPayload = () =>
        order.map((x) => ({
            name: x.name,
            temp: x.temp || 'Hot',
            milk: x.milk || undefined,
            quantity: x.quantity,
        }));

    const handlePayOnline = async () => {
        const err = validateForm();
        if (err) { setFormError(err); return; }
        setFormError('');
        setSubmitting(true);
        try {
            const fn = httpsCallable(functions, 'createDrinkCheckoutSession');
            const res = await fn({
                customerName: custName.trim(),
                customerEmail: custEmail.trim(),
                items: itemsPayload(),
                origin: window.location.origin,
            });
            if (res.data && res.data.url) {
                window.location.href = res.data.url;
            } else {
                throw new Error('No checkout URL');
            }
        } catch (e) {
            console.error(e);
            setFormError('Could not start online payment. Please try again.');
            setSubmitting(false);
        }
    };

    const handlePayInPerson = async () => {
        const err = validateForm();
        if (err) { setFormError(err); return; }
        setFormError('');
        setSubmitting(true);
        try {
            const fn = httpsCallable(functions, 'submitDrinkOrder');
            const res = await fn({
                customerName: custName.trim(),
                customerEmail: custEmail.trim(),
                items: itemsPayload(),
            });
            const oid = res.data && res.data.orderId;
            navigate(oid ? `/order-received?orderId=${encodeURIComponent(oid)}` : '/order-received');
        } catch (e) {
            console.error(e);
            setFormError('Could not place your order. Please try again.');
            setSubmitting(false);
        }
    };

    const renderItemActions = (item, category) => {
        const cents = priceToCents(item.price);
        if (cents == null) return null;

        const milkDrink = isMilkDrink(item.name);
        const availMilkNames = milkOptions.filter((m) => m.available).map((m) => m.name);
        const chosen = milkChoice[item.name];
        const selMilk = availMilkNames.includes(chosen)
            ? chosen
            : (availMilkNames[0] || (milkOptions[0] && milkOptions[0].name));
        const milkForOrder = milkDrink ? selMilk : null;

        // If the admin-set name already contains "milk", don't tack on another.
        const withMilkLabel = (name) => (/milk/i.test(name) ? name : `${name} Milk`);
        const milkSelector = milkDrink && milkOptions.length > 0 ? (
            <div className='ourmenu-milk'>
                <select
                    className='ourmenu-milk-select'
                    value={selMilk}
                    onChange={(e) =>
                        setMilkChoice((prev) => ({ ...prev, [item.name]: e.target.value }))
                    }
                >
                    {milkOptions.map((m) => (
                        <option
                            key={m.name}
                            value={m.name}
                            disabled={!m.available}
                            style={!m.available ? { color: '#b0b0b0' } : undefined}
                        >
                            {withMilkLabel(m.name)}{!m.available ? ' — unavailable' : ''}
                        </option>
                    ))}
                </select>
            </div>
        ) : null;

        const inCoffee = isCoffeeSection(category);
        const hotOnly = isHotOnly(item.name);
        const coldOnly = isColdOnly(item.name);
        const hasBothTemps = inCoffee && !hotOnly && !coldOnly;

        // Selected temp — '' if user hasn't chosen yet (dropdown shows "Hot/Ice" placeholder).
        // If they never pick, Add to Cart defaults to Hot.
        const selectedTemp = tempChoice[item.name] || '';
        const tempForOrder = selectedTemp || (coldOnly ? 'Iced' : 'Hot');

        const tempSelector = hasBothTemps ? (
            <select
                className='ourmenu-add-select cafemenu-temp-select'
                value={selectedTemp}
                onChange={(e) =>
                    setTempChoice((prev) => ({ ...prev, [item.name]: e.target.value }))
                }
            >
                <option value='' disabled>Hot/Ice</option>
                <option value='Hot'>Hot</option>
                <option value='Iced'>Ice</option>
            </select>
        ) : null;

        // Read-only label for single-temp coffee (Cappuccino → Hot only,
        // Cold Brew / Nitro → Ice only). No dropdown, just an indicator.
        const tempLock = (inCoffee && (hotOnly || coldOnly)) ? (
            <span className='cafemenu-temp-lock'>
                {hotOnly ? 'Hot only' : 'Ice only'}
            </span>
        ) : null;

        return (
            <div className='ourmenu-add'>
                {milkSelector}
                {tempSelector}
                {tempLock}
                <button
                    className='ourmenu-add-btn cafemenu-cart-btn'
                    onClick={() => addToOrder(item.name, cents, tempForOrder, milkForOrder)}
                >
                    Add to Cart
                </button>
            </div>
        );
    };

    return (
        <div className='cafemenu-page'>
            <div className='main-content'>
                <div className='ourmenu-container'>
                    <div className='ourmenu-intro'>
                        <h1>COFFEE &amp; FOOD MENU</h1>
                        <p>All beans are roasted in-house</p>
                    </div>

                    {menu.map((section) => {
                        const visibleItems = (section.items || []).filter((it) => it.hidden !== true);
                        if (visibleItems.length === 0) return null;
                        return (
                            <section className='ourmenu-section' key={section.category}>
                                <header className='cafemenu-section-head'>
                                    <h2 className='ourmenu-category'>{section.category}</h2>
                                    {section.note && (
                                        <p className='ourmenu-note'>{section.note}</p>
                                    )}
                                </header>
                                <ul className='ourmenu-list'>
                                    {visibleItems.map((item) => {
                                        const cents = priceToCents(item.price);
                                        // Coffee items keep the layout clean — no descriptions rendered.
                                        // Every other section shows the desc (first line = origin/meta if a
                                        // newline is present, remainder = tasting notes).
                                        const showDesc = !isCoffeeSection(section.category);
                                        const descLines = showDesc
                                            ? (item.desc || '').split('\n').map((s) => s.trim()).filter(Boolean)
                                            : [];
                                        const meta = descLines.length > 1 ? descLines[0] : null;
                                        const notes = descLines.length > 1 ? descLines.slice(1).join(' ') : (descLines[0] || null);
                                        return (
                                            <li className='ourmenu-item' key={item.name}>
                                                <div className='ourmenu-item-head'>
                                                    <span className='ourmenu-item-name'>{item.name}</span>
                                                    <span className='ourmenu-item-dots' />
                                                    {cents != null && (
                                                        <span className='ourmenu-item-price'>{money(cents)}</span>
                                                    )}
                                                </div>
                                                {meta && (
                                                    <p className='ourmenu-item-desc cafemenu-item-meta'>{meta}</p>
                                                )}
                                                {notes && (
                                                    <p className='ourmenu-item-desc'>{notes}</p>
                                                )}
                                                <div className='cafemenu-actions'>{renderItemActions(item, section.category)}</div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        );
                    })}
                </div>
            </div>

            {/* Sticky order bar */}
            {orderCount > 0 && !showCheckout && (
                <div className='ourmenu-bar'>
                    <div className='ourmenu-bar__info'>
                        <strong>{orderCount}</strong> {orderCount === 1 ? 'item' : 'items'} · {money(orderTotal)}
                    </div>
                    <button className='ourmenu-bar__btn' onClick={() => setShowCheckout(true)}>
                        REVIEW &amp; CHECK OUT
                    </button>
                </div>
            )}

            {/* Checkout modal */}
            {showCheckout && (
                <div className='ourmenu-modal' onClick={() => !submitting && setShowCheckout(false)}>
                    <div className='ourmenu-modal__panel' onClick={(e) => e.stopPropagation()}>
                        <button
                            className='ourmenu-modal__close'
                            onClick={() => !submitting && setShowCheckout(false)}
                            aria-label='Close'
                        >
                            ✕
                        </button>
                        <h2 className='ourmenu-modal__title'>Your Order</h2>

                        {order.length === 0 ? (
                            <p className='ourmenu-modal__empty'>Your order is empty.</p>
                        ) : (
                            <div className='ourmenu-modal__items'>
                                {order.map((x, idx) => (
                                    <div className='ourmenu-oitem' key={`${x.name}-${x.temp}-${x.milk || ''}`}>
                                        <div className='ourmenu-oitem__name'>
                                            {x.name}{' '}
                                            {x.temp && <span className='ourmenu-oitem__temp'>{x.temp}</span>}
                                            {x.milk && <span className='ourmenu-oitem__temp'>{x.milk}</span>}
                                        </div>
                                        <div className='ourmenu-oitem__qty'>
                                            <button onClick={() => changeQty(idx, -1)}>−</button>
                                            <span>{x.quantity}</span>
                                            <button onClick={() => changeQty(idx, 1)}>+</button>
                                        </div>
                                        <div className='ourmenu-oitem__price'>{money(x.unit * x.quantity)}</div>
                                    </div>
                                ))}
                                <div className='ourmenu-modal__total'>
                                    <span>Total</span>
                                    <span>{money(orderTotal)}</span>
                                </div>
                            </div>
                        )}

                        <div className='ourmenu-form'>
                            <label>Name</label>
                            <input
                                type='text'
                                value={custName}
                                onChange={(e) => setCustName(e.target.value)}
                                placeholder='Your name'
                                disabled={submitting}
                            />
                            <label>Email (optional)</label>
                            <input
                                type='email'
                                value={custEmail}
                                onChange={(e) => setCustEmail(e.target.value)}
                                placeholder='you@example.com'
                                disabled={submitting}
                            />
                            {formError && <p className='ourmenu-form__error'>{formError}</p>}

                            <div className='ourmenu-pay'>
                                <button
                                    className='ourmenu-pay__online'
                                    onClick={handlePayOnline}
                                    disabled={submitting || order.length === 0}
                                >
                                    {submitting ? 'PLEASE WAIT…' : `PAY ONLINE — ${money(orderTotal)}`}
                                </button>
                                <button
                                    className='ourmenu-pay__person'
                                    onClick={handlePayInPerson}
                                    disabled={submitting || order.length === 0}
                                >
                                    {submitting ? 'PLEASE WAIT…' : 'PAY IN PERSON (CASH OR CARD)'}
                                </button>
                            </div>
                            <p className='ourmenu-pay__note'>
                                Pay online by card now, or place the order and pay at pickup with
                                cash or credit card.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
}

export default CafeMenu;
